import {
	combineValidators,
	composeValidators,
	createValidator,
	hasLengthGreaterThan,
	hasLengthLessThan,
	isRequired,
	isRequiredIf,
} from "revalidate";
import {
	creditCardValidator,
	emailFieldValidator,
	firstnameValidator,
	lastnameValidator,
	phoneFieldValidator,
} from "app/utils/form/fieldValidators";
import {
	customIsRequiredIf,
	isAmount,
	isCreditCardValid,
	mustAcceptTerms,
} from "app/utils/form/validators";
import { CIVILITY, CREDIT_CARDS_TYPES, PAYMENT_METHODS } from "app/constants";
import { determineCardType } from "app/utils/creditCardUtils";
import get from "lodash/get";
import { getQuoteTotalWithDelta } from "app/pages/Booking/bookingSelectors";

export const contactFields = [
	"contact.name.gender",
	"contact.name.firstName",
	"contact.name.lastName",
	"contact.address.line1",
	"contact.address.line2",
	"contact.address.postCode",
	"contact.address.city",
	"contact.address.country",
	"contact.address.phone",
	"contact.email",
	"contact.phone",
];

export const paymentFields = [
	"payment.type",
	"payment.mode",
	"payment.issuerId",
	"payment.card.maidenName",
	"payment.card.birthdate.day",
	"payment.card.birthdate.month",
	"payment.card.birthdate.year",
	"payment.card.zipCodeOfBirth",
	"payment.card.encrypted",
	"payment.card.holderName",
	"payment.card.expiryMonth",
	"payment.card.expiryYear",
	"payment.card.cvc",
	"payment.card.number",
	"payment.card.amount",
	"payment.cardBCMC.maidenName",
	"payment.cardBCMC.birthdate.day",
	"payment.cardBCMC.birthdate.month",
	"payment.cardBCMC.birthdate.year",
	"payment.cardBCMC.zipCodeOfBirth",
	"payment.cardBCMC.encrypted",
	"payment.cardBCMC.holderName",
	"payment.cardBCMC.expiryMonth",
	"payment.cardBCMC.expiryYear",
	"payment.cardBCMC.cvc",
	"payment.cardBCMC.number",
	"payment.swissBilling.birthdate.day",
	"payment.swissBilling.birthdate.month",
	"payment.swissBilling.birthdate.year",
	"payment.swissBilling.invoiceType",
];

export const dependantPaymentCardFields = ["expiryMonth", "expiryYear", "cvc", "number"];

export const passengerFields = [
	"passengers[].type",
	"passengers[].index",
	"passengers[].name.gender",
	"passengers[].name.firstName",
	"passengers[].name.lastName",
	"passengers[].birthdate.day",
	"passengers[].birthdate.month",
	"passengers[].birthdate.year",
	"passengers[].frequentFlyerTravellerCardNumber",
	// Passport retiré de l apage de payment pour le moment
	// "passengers[].passport.passportNumber",
	// "passengers[].passport.countryIssue",
	// "passengers[].passport.expirationMonth",
	// "passengers[].passport.expirationYear",
];

export const insuranceField = ["insurance"];

export const termsField = ["terms"];

export const couponsFields = ["coupon"];

export const fields = [].concat(
	contactFields,
	paymentFields,
	passengerFields,
	couponsFields,
	insuranceField,
	termsField,
	"uniqueNamePassengers"
);

export const validate = (values, state) => {
	const paymentTypes = state.payment.paymentTypes;
	const selectedPaymentTypeValue = state.booking.paymentType;
	const selectedPaymentModeValue = state.booking.paymentMode;

	const selectedPaymentType = paymentTypes.find(paymentType => {
		return (
			paymentType.paymentType === selectedPaymentTypeValue &&
			paymentType.terms === selectedPaymentModeValue
		);
	});

	const acceptedCreditCardTypes =
		selectedPaymentType && selectedPaymentType.acceptedCreditCardTypes;

	let paymentValidators = {};
	if (selectedPaymentTypeValue === PAYMENT_METHODS.CASINO) {
		paymentValidators = {
			card: {
				...(values.contact.name.gender !== CIVILITY.MALE && {
					maidenName: customIsRequiredIf({
						message: { id: "error.payment.maiden.name.required" },
					}),
				}),
				birthdate: {
					day: customIsRequiredIf({
						message: { id: "error.birthdate.day" },
					}),
					month: customIsRequiredIf({
						message: { id: "error.birthdate.month" },
					}),
					year: customIsRequiredIf({
						message: { id: "error.birthdate.year" },
					}),
				},
				zipCodeOfBirth: composeValidators(
					isRequired({
						message: { id: "error.payment.zip.code.of.birth.required" },
					}),
					hasLengthGreaterThan(1)({
						message: { id: "error.departement.code.required" },
					})
				)("departement number"),
				holderName: isRequired({
					message: { id: "error.payment.card.holder.name.required" },
				}),
				expiryMonth: composeValidators(
					isRequired({
						message: { id: "error.payment.card.expiryMonth.required" },
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: { id: "error.payment.credit.card.invalid" },
					})
				)("expiryMonth"),
				expiryYear: composeValidators(
					isRequired({
						message: { id: "error.payment.card.expiryYear.required" },
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: { id: "error.payment.credit.card.invalid" },
					})
				)("expiryYear"),
				cvc: composeValidators(
					isRequired({
						message: { id: "error.payment.card.cvc.required" },
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: { id: "error.payment.credit.card.invalid" },
					})
				)("cvc"),
				number: creditCardValidator(acceptedCreditCardTypes),
			},
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.CARD) {
		paymentValidators = {
			card: {
				amount: isAmount({
					message: { id: "error.payment.card.amount.format" },
				}),
				holderName: isRequired({
					message: { id: "error.payment.card.holder.name.required" },
				}),
				expiryMonth: composeValidators(
					isRequired({
						message: { id: "error.payment.card.expiryMonth.required" },
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: { id: "error.payment.credit.card.invalid" },
					})
				)("expiryMonth"),
				expiryYear: composeValidators(
					isRequired({
						message: { id: "error.payment.card.expiryYear.required" },
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: { id: "error.payment.credit.card.invalid" },
					})
				)("expiryYear"),
				cvc: composeValidators(
					isRequiredIf(
						() =>
							determineCardType(get(values, "payment.card.number")) !==
							CREDIT_CARDS_TYPES.MAESTRO
					)({
						message: { id: "error.payment.card.cvc.required" },
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: { id: "error.payment.credit.card.invalid" },
					})
				)("cvc"),
				number: creditCardValidator(acceptedCreditCardTypes),
			},
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.BCMC) {
		paymentValidators = {
			cardBCMC: {
				holderName: isRequired({
					message: { id: "error.payment.card.holder.name.required" },
				}),
				expiryMonth: composeValidators(
					isRequired({
						message: { id: "error.payment.card.expiryMonth.required" },
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: { id: "error.payment.credit.card.invalid" },
					})
				)("expiryMonth"),
				expiryYear: composeValidators(
					isRequired({
						message: { id: "error.payment.card.expiryYear.required" },
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: { id: "error.payment.credit.card.invalid" },
					})
				)("expiryYear"),
				number: creditCardValidator(["bcmc"]),
			},
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.IDEAL) {
		paymentValidators = {
			issuerId: isRequired({
				message: { id: "error.payment.issuerId.required" },
			}),
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.SWISS_BILLING) {
		paymentValidators = {
			swissBilling: {
				birthdate: {
					day: customIsRequiredIf({
						message: { id: "error.birthdate.day" },
					}),
					month: customIsRequiredIf({
						message: { id: "error.birthdate.month" },
					}),
					year: customIsRequiredIf({
						message: { id: "error.birthdate.year" },
					}),
				},
				invoiceType: isRequired({
					message: { id: "error.payment.invoiceType.required" },
				}),
			},
		};
	}

	const validators = {
		contact: {
			name: {
				gender: isRequired({
					message: { id: "error.gender.required" },
				}),
				firstName: firstnameValidator,
				lastName: lastnameValidator,
			},
			address: {
				line1: isRequired({
					message: { id: "error.address.line1.required" },
				}),
				postCode: isRequired({
					message: { id: "error.address.postCode.required" },
				}),
				city: composeValidators(
					isRequired({
						message: { id: "error.address.city.required" },
					}),
					hasLengthLessThan(51)({
						message: { id: "error.address.city.maxlength" },
					})
				)("city"),
				country: isRequired({
					message: { id: "error.address.country.required" },
				}),
			},
			email: emailFieldValidator,
			phone: phoneFieldValidator,
		},
		"passengers[]": createValidator(
			() => passenger => ({
				name: combineValidators({
					lastName: lastnameValidator,
					firstName: firstnameValidator,
					gender: isRequired({
						message: { id: "error.gender.required" },
					}),
				})({
					...passenger.name,
					passengers: values.passengers,
					index: passenger.index,
				}),
				...(state.payment.dateOfBirthRequired && {
					birthdate: combineValidators({
						day: customIsRequiredIf({
							message: { id: "error.birthdate.day" },
						}),
						month: customIsRequiredIf({
							message: { id: "error.birthdate.month" },
						}),
						year: customIsRequiredIf({
							message: { id: "error.birthdate.year" },
						}),
					})({ ...passenger.birthdate }),
				}),
			}),
			""
		)(values.passengers),
		insurance: isRequired({
			message: { id: "error.payment.insurance.required" },
		}),
		terms: composeValidators(
			mustAcceptTerms({
				message: { id: "error.must.accept.terms" },
			})
		)("Terms"),
	};

	const cartAmount = getQuoteTotalWithDelta(state);

	if (cartAmount > 0) {
		validators.payment = paymentValidators;
	}

	return combineValidators(validators)(values);
};
